import React, {useState, useEffect, useCallback, useMemo} from "react";
import {isSyncEnabled, syncPush, syncPull} from "../pages/generic/crud";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../utils/toast/Toast";
import {withSyncDot} from "../column-formatters/SyncStatusColumnFormatter";
import {SyncProgressDialog} from "../generic-components/SyncProgressDialog";

/**
 * Generic hook for handling sync operations on any listing page.
 * Automatically checks if entity has @SyncRequired annotation and provides:
 * - Sync buttons for toolbar (conditionally based on push/pull capabilities)
 * - Sync dot wrapper for first column
 * - Sync progress dialog component
 *
 * @param {number} entityId - The entity type ID
 * @param {function} onSyncComplete - Optional callback after sync completes
 * @returns {object} - Sync state, buttons, column config, and dialog component
 */
export function useGenericSync(entityId, onSyncComplete) {
    const [syncEnabled, setSyncEnabled] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pullEnabled, setPullEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [syncType, setSyncType] = useState(null);
    const [isInProgress, setIsInProgress] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    // Check if entity is sync-enabled on mount
    useEffect(() => {
        let mounted = true;

        const checkSyncEnabled = async () => {
            try {
                setLoading(true);
                const response = await isSyncEnabled(entityId);
                if (mounted) {
                    setSyncEnabled(response.data.syncEnabled === true);
                    setPushEnabled(response.data.pushEnabled === true);
                    setPullEnabled(response.data.pullEnabled === true);
                }
            } catch (error) {
                console.error("Error checking sync enabled:", error);
                if (mounted) {
                    setSyncEnabled(false);
                    setPushEnabled(false);
                    setPullEnabled(false);
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        if (entityId) {
            checkSyncEnabled();
        } else {
            setLoading(false);
            setSyncEnabled(false);
            setPushEnabled(false);
            setPullEnabled(false);
        }

        return () => {
            mounted = false;
        };
    }, [entityId]);

    // Push to server
    const pushToServer = useCallback(async () => {
        setSyncType('push');
        setSyncResult(null);
        setIsInProgress(true);
        setShowDialog(true);

        try {
            const response = await syncPush(entityId);
            const result = response.data;
            setSyncResult(result);

            if (result.success) {
                showToast(`Push successful! ${result.successCount} records synced.`, TOAST_SUCCESS);
            } else {
                showToast(`Push failed: ${result.message}`, TOAST_FAILURE);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            setSyncResult({
                success: false,
                message: `Push failed: ${errorMsg}`,
                totalProcessed: 0,
                successCount: 0,
                failedCount: 0
            });
            showToast(`Push failed: ${errorMsg}`, TOAST_FAILURE);
        } finally {
            setIsInProgress(false);
            if (onSyncComplete) {
                onSyncComplete();
            }
        }
    }, [entityId, onSyncComplete]);

    // Pull from server
    const pullFromServer = useCallback(async () => {
        setSyncType('pull');
        setSyncResult(null);
        setIsInProgress(true);
        setShowDialog(true);

        try {
            const response = await syncPull(entityId);
            const result = response.data;
            setSyncResult(result);

            if (result.success) {
                const counts = `Inserted: ${result.insertedCount || 0}, Updated: ${result.updatedCount || 0}, Deleted: ${result.deletedCount || 0}`;
                showToast(`Pull successful! ${counts}`, TOAST_SUCCESS);
            } else {
                showToast(`Pull failed: ${result.message}`, TOAST_FAILURE);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            setSyncResult({
                success: false,
                message: `Pull failed: ${errorMsg}`,
                totalCount: 0,
                insertedCount: 0,
                updatedCount: 0,
                deletedCount: 0
            });
            showToast(`Pull failed: ${errorMsg}`, TOAST_FAILURE);
        } finally {
            setIsInProgress(false);
            if (onSyncComplete) {
                onSyncComplete();
            }
        }
    }, [entityId, onSyncComplete]);

    // Close dialog
    const closeDialog = useCallback(() => {
        if (!isInProgress) {
            setShowDialog(false);
            setSyncResult(null);
            setSyncType(null);
        }
    }, [isInProgress]);

    // Sync toolbar buttons (conditionally based on push/pull capabilities)
    const syncToolbarButtons = useMemo(() => {
        if (!syncEnabled) return [];

        const buttons = [];

        // Only show Push button if push is enabled for this entity
        if (pushEnabled) {
            buttons.push({
                name: "Push to Server",
                color: "info",
                icon: "fa fa-cloud-upload-alt",
                onClick: pushToServer,
            });
        }

        // Only show Pull button if pull is enabled for this entity
        if (pullEnabled) {
            buttons.push({
                name: "Pull from Server",
                color: "success",
                icon: "fa fa-cloud-download-alt",
                onClick: pullFromServer,
            });
        }

        return buttons;
    }, [syncEnabled, pushEnabled, pullEnabled, pushToServer, pullFromServer]);

    // Wrap a column formatter with a sync dot (only if sync is enabled)
    const wrapWithSyncDot = useCallback((formatter) => {
        if (!syncEnabled) return formatter;
        return withSyncDot(formatter);
    }, [syncEnabled]);

    // Sync Progress Dialog component
    const SyncDialog = useCallback(() => {
        if (!syncEnabled) return null;

        return (
            <SyncProgressDialog
                show={showDialog}
                onClose={closeDialog}
                syncType={syncType}
                isInProgress={isInProgress}
                syncResult={syncResult}
            />
        );
    }, [syncEnabled, showDialog, closeDialog, syncType, isInProgress, syncResult]);

    return {
        // State
        syncEnabled,
        pushEnabled,
        pullEnabled,
        loading,
        showDialog,
        syncType,
        isInProgress,
        syncResult,

        // Actions
        pushToServer,
        pullFromServer,
        closeDialog,

        // Toolbar buttons (spread into toolBarButtons array)
        syncToolbarButtons,

        // Sync dot wrapper for first column formatter
        wrapWithSyncDot,

        // Dialog component
        SyncDialog
    };
}
